import { Button, TextInput } from '@components/v2/ui';
import { LanguageSelect } from '@components/v2/ui/LanguageSelect/LanguageSelect';
import {
	REGEX_EMAIL,
	REGEX_PHONE_NUMBER,
} from '@config/constants/regex';
import { useCreateContact } from '@features/offsets/api/create-contact';
import { queryClient } from '@lib/react-query';
import {
	Drawer,
	DrawerProps,
	Flex,
	SimpleGrid,
	Stack,
} from '@mantine/core';
import {
	isNotEmpty,
	matches,
	useForm,
} from '@mantine/form';
import useStore from '@store/index';
import { useTranslation } from 'react-i18next';
import classes from './DrawerCreateContact.module.css';

/* -------------------------------------------------------------------------- */
/*                                  Constants                                 */
/* -------------------------------------------------------------------------- */
const LANGUAGES_OPTIONS = [
	{
		label: 'Français',
		value: 'fr',
		flagUrl: 'https://flagcdn.com/w80/fr.png',
	},
	{
		label: 'English',
		value: 'en',
		flagUrl: 'https://flagcdn.com/w80/gb.png',
	},
];

interface ContactFormValues {
	firstName: string;
	lastName: string;
	email: string;
	language: string;
	phoneNumber: string;
}

interface CreateContactPayload {
	firstName: string;
	lastName: string;
	email: string;
	locale: {
		language: string;
	};
	phoneNumber?: string;
	organization: string;
}

const DrawerCreateContact = (
	props: DrawerProps & {
		onSuccess?: (values: any) => void;
	}
) => {
	const { t } = useTranslation();
	/* ******************************** Store States ******************************* */
	const { setCreatedUser, selectedOrganizationId } =
		useStore();

	const createContactMutation = useCreateContact({
		mutationConfig: {
			onSuccess: (data) => {
				queryClient.invalidateQueries({
					queryKey: [
						'users',
						{
							organization: selectedOrganizationId,
							queryParams: {
								organization: selectedOrganizationId,
							},
						},
					],
				});
				setCreatedUser(data.id.toString());
				form.reset();
				props.onClose();
			},
			onError() {},
		},
	});

	const form = useForm({
		mode: 'uncontrolled',
		initialValues: {
			firstName: '',
			lastName: '',
			email: '',
			language: 'fr',
			phoneNumber: '',
		},
		validate: {
			firstName: isNotEmpty(
				t('global.firstNameRequired')
			),
			lastName: isNotEmpty(t('global.lastNameRequired')),
			email: matches(
				REGEX_EMAIL,
				t('form.error.invalidEmail')
			),
			phoneNumber: matches(
				REGEX_PHONE_NUMBER,
				t('form.error.invalidPhoneNumber')
			),
		},
	});

	const handleSubmit = (values: ContactFormValues) => {
		const { language, phoneNumber, ...rest } = values;

		const payload: CreateContactPayload = {
			...rest,
			locale: { language },
			organization: selectedOrganizationId,
		};

		if (phoneNumber && phoneNumber.trim() !== '') {
			payload.phoneNumber = phoneNumber;
		}

		createContactMutation.mutate({ body: payload });
	};

	const handleOnClose = () => {
		form.reset();
		form.clearErrors();
		props.onClose();
	};

	return (
		<Drawer
			opened={props.opened}
			onClose={handleOnClose}
			title={t('drawerCreateContact.title')}
			position="left"
			size="656px"
			zIndex={99}
			classNames={{
				...classes,
				title: classes.customTitle,
			}}
		>
			<form onSubmit={form.onSubmit(handleSubmit)}>
				<Stack h={'84vh'} justify="space-between">
					<Stack gap="16px">
						<SimpleGrid cols={2}>
							<TextInput
								withAsterisk
								key={form.key('firstName')}
								label={t('global.firstName')}
								placeholder={t('global.firstName')}
								{...form.getInputProps('firstName')}
							/>
							<TextInput
								withAsterisk
								key={form.key('lastName')}
								label={t('global.lastName')}
								placeholder={t('global.lastName')}
								{...form.getInputProps('lastName')}
							/>
						</SimpleGrid>

						<TextInput
							withAsterisk
							label="E-mail"
							placeholder="E-mail"
							key={form.key('email')}
							{...form.getInputProps('email')}
						/>

						<SimpleGrid cols={2}>
							<LanguageSelect
								withAsterisk
								value={form.values.language}
								onChange={(val) =>
									form.setFieldValue('language', val)
								}
								options={LANGUAGES_OPTIONS}
								fullWidth={false}
								label={t('global.language')}
							/>

							<TextInput
								key={form.key('phoneNumber')}
								label={t('global.phoneNumber')}
								placeholder={'ex : +33345435324'}
								{...form.getInputProps('phoneNumber')}
							/>
						</SimpleGrid>
					</Stack>

					<Flex
						justify="space-between"
						gap={'12px'}
						mt="xl"
					>
						<Button
							fullWidth
							variant="outline"
							onClick={props.onClose}
							style={{
								borderRadius: '24px',
								height: '48px',
							}}
						>
							{t('global.cancel')}
						</Button>
						<Button
							type="submit"
							fullWidth
							variant="primary"
							style={{
								borderRadius: '24px',
								height: '48px',
								backgroundColor: '#2B3D63',
							}}
						>
							{t('drawerCreateContact.btnValidation')}
						</Button>
					</Flex>
				</Stack>
			</form>
		</Drawer>
	);
};

export default DrawerCreateContact;
